import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useCRM } from '../../contexts/CRMContext';
import crmApi from '../../utils/crmApi';

const AgencyBilling = ({ agencyId: agencyIdProp }) => {
  const params = useParams();
  const agencyId = agencyIdProp || params.agencyId;
  const { currentAgency } = useCRM();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await crmApi.get(`/agencies/${agencyId}/billing`);
        if (res.data?.success) setData(res.data.data);
        else setError(res.data?.message || 'Failed to load billing');
      } catch (e) {
        setError(e.response?.data?.message || 'Failed to load billing');
      } finally {
        setLoading(false);
      }
    };
    if (agencyId) load();
  }, [agencyId]);

  const openPortal = async () => {
    try {
      setBusy(true);
      const res = await crmApi.post(`/agencies/${agencyId}/billing/portal`, {});
      if (res.data?.success && res.data.url) window.location.href = res.data.url;
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to open portal');
    } finally {
      setBusy(false);
    }
  };

  const startCheckout = async (priceId, quantity) => {
    try {
      setBusy(true);
      const res = await crmApi.post(`/agencies/${agencyId}/billing/checkout`, { priceId, quantity });
      if (res.data?.success && res.data.url) window.location.href = res.data.url;
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to start checkout');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse h-6 w-48 bg-gray-200 rounded mb-4"></div>
        <div className="animate-pulse h-4 w-80 bg-gray-200 rounded"></div>
      </div>
    );
  }

  if (error) {
    return <div className="p-6 text-red-600">{error}</div>;
  }

  return (
    <div className="max-w-4xl p-6 mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Billing for {currentAgency?.name}</h1>
        <p className="text-sm text-muted-foreground">Plan, seats, and add-ons are managed per agency.</p>
      </div>

      <div className="bg-card border border-border rounded-lg p-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm text-muted-foreground">Current Plan</div>
            <div className="text-xl font-semibold capitalize">{data?.planTier || 'trial'}</div>
            <div className="text-xs mt-1">Status: {data?.status}</div>
          </div>
          <div className="flex gap-2">
            <button onClick={openPortal} disabled={busy} className="px-3 py-2 text-sm bg-blue-600 text-white rounded">
              Manage billing
            </button>
            {/* Example change plan button (requires a real priceId) */}
            {/* <button onClick={() => startCheckout('price_xxx', 1)} disabled={busy} className="px-3 py-2 text-sm border rounded">Change plan</button> */}
          </div>
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg p-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <div className="text-sm text-muted-foreground">Team seats (staff)</div>
            <div className="text-2xl font-semibold">
              {data?.staffSeatsUsed ?? 0}
              {" "}
              <span className="text-muted-foreground text-base">of {((data?.featureFlags?.maxSeats === -1) || (data?.seatLimit === -1)) ? 'Unlimited' : (data?.seatLimit ?? 0)}</span>
            </div>
            <div className="h-2 w-full bg-muted rounded">
              <div
                className="h-2 bg-blue-600 rounded"
                style={{ width: (() => {
                  const max = (data?.featureFlags?.maxSeats === -1 || data?.seatLimit === -1) ? null : (data?.seatLimit || 0);
                  if (!max || max <= 0) return '0%';
                  const used = data?.staffSeatsUsed || 0;
                  return Math.min(100, Math.round((used / max) * 100)) + '%';
                })() }}
              />
            </div>
          </div>
          <div className="space-y-2">
            <div className="text-sm text-muted-foreground">Client users (portal)</div>
            <div className="text-2xl font-semibold">{data?.clientUsersUsed ?? 0}</div>
            <div className="text-xs text-muted-foreground">Portal users do not consume staff seats.</div>
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <button onClick={openPortal} disabled={busy} className="px-3 py-2 text-sm border rounded">
            Manage seats
          </button>
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg p-5">
        <div className="text-sm text-muted-foreground mb-3">Plan Limits</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {(() => {
            const ff = data?.featureFlags || {};
            const fmt = (v) => (v === -1 ? 'Unlimited' : (typeof v === 'number' ? v : '—'));
            const limits = [
              { label: 'Max seats', value: ff.maxSeats, icon: '👥' },
              { label: 'Max agencies', value: ff.maxAgencies, icon: '🏢' },
              { label: 'Max clients', value: ff.maxClients, icon: '🧑‍💼' },
              { label: 'Max accounts', value: ff.maxAccounts, icon: '📁' }
            ];
            return limits.map(item => (
              <div key={item.label} className="flex items-center justify-between rounded border border-border px-3 py-2">
                <div className="flex items-center gap-2">
                  <span className="text-base">{item.icon}</span>
                  <span className="text-sm text-muted-foreground">{item.label}</span>
                </div>
                <span className="text-sm font-medium">{fmt(item.value)}</span>
              </div>
            ));
          })()}
        </div>
        {Array.isArray(data?.featureFlags?.allowedRoles) && (
          <div className="mt-4 text-sm">
            <div className="text-muted-foreground mb-2">Allowed roles</div>
            <div className="flex flex-wrap gap-2">
              {data.featureFlags.allowedRoles.map(r => (
                <span key={r} className="px-2 py-0.5 rounded-full bg-muted text-foreground text-xs capitalize">{r.replace('_',' ')}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="bg-card border border-border rounded-lg p-5">
        <div className="text-sm text-muted-foreground mb-3">Add-ons & Features</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
          {Object.entries(data?.featureFlags || {})
            .filter(([, v]) => typeof v === 'boolean')
            .map(([key, enabled]) => (
              <div key={key} className="flex items-center gap-2 text-sm">
                <span className={`inline-flex items-center justify-center h-4 w-4 rounded ${enabled ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                <span className="capitalize">{key.replace(/([A-Z])/g, ' $1')}</span>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default AgencyBilling;


